import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import { Viewer } from "@react-pdf-viewer/core";
import moment from "moment";

import StepIndicator from "../../Components/Others/step-indicator";
import TextField from "../../Components/Form Fields/text-field";
import SSNField from "../../Components/Form Fields/ssn-field";
import DatePickerField from "../../Components/Form Fields/date-picker-field";
import SelectField from "../../Components/Form Fields/select-field";
import AddressAutoCompleteField from "../../Components/Form Fields/address-autocomplete-field";
import CheckBox from "../../Components/Form Fields/checkbox";
import PrimaryButton from "../../Components/Buttons/primary-button";
import { RegularAmountField } from "../../Components/Form Fields/amount-field";

import renderStakeSaverSetupSuccess from "../../Organisms/Modals/Stake Saver/stake-saver-setup-success";
import renderSetupStakePay2 from "../../Organisms/Modals/Stake Pay 2/setup-stake-pay-2";
import renderFinishStakeSaverSetup from "../../Organisms/Modals/Stake Saver/finish-stake-saver-setup";

import { useStakeDebit, useStakeSaverFormPrefill, useUserAccount } from "../../CustomHooks/Queries";
import { useApplyStakeChecking, useSetupStakeSaver } from "../../CustomHooks/Mutations";

import { useModalContext } from "../../Contexts/ModalContext";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import withFormProvider from "../../HOC/withFormProvider";

import { APIhandler } from "../../Utils/api";
import { validation } from "../../Utils/validations";
import { getStakeDebitAPIObject } from "../../Utils/api-data-transforms";
import {
  dispatchAPIErrorToast,
  dispatchInfoToast,
  extract5DigitZipCode,
  formatCurrency,
  getFormattedSSN,
  getPublicIPAddress,
  getUserId,
  getUserSfid,
  replaceNonNumericCharacters,
  transformCountryCodeFormatToUSNumber,
} from "../../Utils/helpers";
import { UnitOccupationList, USStates } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import { ArrowLeft, CrossMark, Lock } from "../../Assets/Icons";

const PersonalDetails = () => {
  const { register, watch, getFieldState, formState, setValue } = useFormContext();

  const email = watch("email");
  const phoneNumber = watch("phoneNumber");
  const occupation = watch("occupation");
  const address = watch("streetAddress") ?? "";
  const state = watch("state");

  const firstNameProps = register("firstName", {
    required: "First Name is required",
  });

  const lastNameProps = register("lastName", {
    required: "Last Name is required",
  });

  const occupationProps = register("occupation", {
    required: "Occupation is required",
  });

  const addressProps = register("streetAddress", {
    required: "Please enter your street address",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your address must not contain any special characters",
      hasAtleast3Characters: validation.streetAddress.hasAtleast3Characters,
    },
  });

  const aptSuiteUnitProps = register("aptSuiteUnit", {
    validate: {
      isAllowedCharacters: (aptSuiteUnit) =>
        !aptSuiteUnit ||
        validation.address.isAllowedCharacters(aptSuiteUnit) ||
        "Your apt/suite number must not contain any special characters",
      hasAtmost50Characters: validation.aptSuiteUnit.hasAtmost50Characters,
    },
  });

  const cityProps = register("city", {
    required: "Please enter your city",
    validate: {
      isAllowedCharacters: (address) =>
        validation.address.isAllowedCharacters(address) || "Your city must not contain any special characters",
      hasAtleast3Characters: validation.city.hasAtleast2Characters,
    },
  });

  const stateProps = register("state", {
    required: "Please choose your state from the list",
  });

  const zipCodeProps = register("zipCode", {
    required: "Please enter your ZIP code",
    validate: validation.zipcode,
  });

  const { error: firstNameFieldError } = getFieldState(firstNameProps.name, formState);
  const { error: lastNameFieldError } = getFieldState(lastNameProps.name, formState);
  const { error: occupationFieldError } = getFieldState(occupationProps.name, formState);
  const { error: addressFieldError } = getFieldState(addressProps.name, formState);
  const { error: aptSuiteUnitFieldError } = getFieldState(aptSuiteUnitProps.name, formState);
  const { error: cityFieldError } = getFieldState(cityProps.name, formState);
  const { error: stateFieldError } = getFieldState(stateProps.name, formState);
  const { error: zipCodeFieldError } = getFieldState(zipCodeProps.name, formState);

  return (
    <>
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="First Name"
            {...firstNameProps}
            errorMessage={firstNameFieldError?.message}
            error={!!firstNameFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="Last Name"
            {...lastNameProps}
            errorMessage={lastNameFieldError?.message}
            error={!!lastNameFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField className="filled" label="Email" value={email} disabled={true} />
        </Col>
        <Col sm={6} xs={12}>
          <TextField className="filled" label="Phone Number" value={phoneNumber} disabled={true} />
        </Col>
        <Col sm={6} xs={12}>
          <DatePickerField
            variant="filled"
            name="dob"
            label="Date of Birth"
            textFieldProps={{ placeholder: "", fullWidth: true }}
            validations={{
              isAValidDate: validation.date.isAValidDate,
              isAbove18YearsOfAge: validation.date.isAbove18YearsOfAge,
            }}
            datePickerProps={{ disableFuture: true }}
            required="Date of Birth is required"
          />
        </Col>
        <Col sm={6} xs={12}>
          <SSNField className="filled" required="Social Security Number is required" />
        </Col>
        <Col xs={12}>
          <SelectField
            className="filled"
            label="Occupation"
            options={UnitOccupationList}
            {...occupationProps}
            value={occupation ?? ""}
            errorMessage={occupationFieldError?.message}
          />
        </Col>
      </Row>
      <AddressAutoCompleteField
        fieldProps={{
          label: "Street Address",
          placeholder: "Enter a location",
          ...addressProps,
          className: "filled",
          value: address,
          errorMessage: addressFieldError?.message,
          error: !!addressFieldError,
        }}
        onAddressSelect={(selectedAddress) => {
          setValue("streetAddress", selectedAddress.address);
          setValue("city", selectedAddress.city);
          setValue("state", selectedAddress.state);
          setValue("zipCode", selectedAddress.zipCode);
        }}
      />
      <Row className="row-gap-5">
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label={
              <>
                Apt, Suite, Unit <span className="text-sm-medium color--american-silver">(Optional)</span>
              </>
            }
            {...aptSuiteUnitProps}
            errorMessage={aptSuiteUnitFieldError?.message}
            error={!!aptSuiteUnitFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="City"
            {...cityProps}
            errorMessage={cityFieldError?.message}
            error={!!cityFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <SelectField
            className="filled"
            label="State/Province"
            options={USStates}
            {...stateProps}
            value={state ?? ""}
            errorMessage={stateFieldError?.message}
            error={!!stateFieldError}
          />
        </Col>
        <Col sm={6} xs={12}>
          <TextField
            className="filled"
            label="Zip Code"
            {...zipCodeProps}
            errorMessage={zipCodeFieldError?.message}
            error={!!zipCodeFieldError}
          />
        </Col>
      </Row>
    </>
  );
};

const Enrollment = () => {
  const { register, getFieldState, getValues, formState } = useFormContext();

  const { data: userAccountData } = useUserAccount();

  const { isLoading: isStakeSaverFormPrefillLoading, data: stakeSaverFormPrefill } = useStakeSaverFormPrefill();

  const nameOfTheUser = useMemo(
    () => `${userAccountData?.data?.data?.user?.firstName} ${userAccountData?.data?.data?.user?.lastName}`,
    [userAccountData?.data?.data?.user],
  );

  const signProps = register("sign", {
    required: "Please enter your signature",
    validate: (sign) =>
      sign?.trim() === nameOfTheUser?.trim() || "Please make sure the signature matches your full legal name",
  });

  const { error: signFieldError } = getFieldState(signProps.name, formState);

  return isStakeSaverFormPrefillLoading ? (
    <div className="loading-content">
      <CircularProgress />
    </div>
  ) : (
    <>
      {stakeSaverFormPrefill?.is_out_of_network_user && (
        <Row className="row-gap-sm-5 row-gap-4">
          <Col xs={12}>
            <RegularAmountField
              name="rentAmount"
              textFieldProps={{ className: "filled", placeholder: "1000.00" }}
              rules={{
                required: "Please enter your base rent",
              }}
              disabled={!!stakeSaverFormPrefill?.prefill_info?.rent_amount}
            />
          </Col>
          <Col sm={6} xs={12}>
            <DatePickerField
              name="leaseStartDate"
              label="Lease Start Date"
              textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
              required="Please pick a lease start date"
              validations={{
                isAValidDate: validation.date.isAValidDate,
              }}
              variant="filled"
              format="MM / DD / YYYY"
              disabled={!!stakeSaverFormPrefill?.prefill_info?.lease_start_date}
            />
          </Col>
          <Col sm={6} xs={12}>
            <DatePickerField
              name="leaseEndDate"
              label="Lease End Date"
              textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
              datePickerProps={{ disablePast: true }}
              required="Please pick a lease end date"
              validations={{
                isAValidDate: validation.date.isAValidDate,
                isGreaterThanLeaseStartDate: (leaseEndDate) =>
                  moment(leaseEndDate) > moment(getValues("leaseStartDate")) ||
                  "Lease end date must be after the start date",
              }}
              variant="filled"
              format="MM / DD / YYYY"
              disabled={!!stakeSaverFormPrefill?.prefill_info?.lease_end_date}
            />
          </Col>
        </Row>
      )}
      <TextField
        {...signProps}
        className="filled"
        label="Signature"
        description="Type your full legal name as your digital signature."
        placeholder={`Sign here as "${nameOfTheUser}"`}
        inputProps={{ style: { fontFamily: "Playball", fontSize: "22px", lineHeight: "32px" } }}
        error={!!signFieldError}
        errorMessage={signFieldError?.message}
      />
      <div className="form-disclaimers">
        <p className="form-acknowledgement mb-2">{stakeSaverFormPrefill?.descriptions?.signature_acknowledgement}</p>
        <p className="form-disclaimer">{stakeSaverFormPrefill?.descriptions?.offer_disclaimer}</p>
      </div>
      <div className="flex-grow-1"></div>
    </>
  );
};

const Disclosures = () => {
  const [enableEsign, setEnableEsign] = useState(false);

  const { watch, register, getFieldState, formState } = useFormContext();

  const eSign = watch("eSign") ?? false;
  const agreeToTerms = watch("agreeToTerms") ?? false;
  // const disclosures = watch("disclosures") ?? false;

  const eSignProps = register("eSign", {
    required: true,
  });

  const agreeToTermsProps = register("agreeToTerms", {
    required: true,
  });

  // const disclosuresProps = register("disclosures", {
  //   required: true,
  // });

  const { error: eSignFieldError } = getFieldState(eSignProps.name, formState);
  const { error: agreeToTermsFieldError } = getFieldState(agreeToTermsProps.name, formState);
  // const { error: disclosuresFieldError } = getFieldState(disclosuresProps.name, formState);

  const handlePageChange = (e) => {
    if (e.currentPage === e.doc.numPages - 1) {
      setEnableEsign(true);
    }
  };

  return (
    <div className="d-flex flex-column row-gap-4">
      <div className="d-flex flex-column row-gap-2">
        <label className="text-sm-medium color--mist mb-0">E-Sign Consent Disclosure</label>
        <p className="text-sm-regular color--american-silver mb-0">
          Please review the E-Sign Consent Disclosure below. You’ll need to scroll to the bottom before you can
          continue.
        </p>
      </div>
      <div className="esign-disclosure-box">
        <Viewer
          fileUrl="https://disclosures.transpecos.bank/terms/electronicconsentdisclosures.pdf"
          onPageChange={handlePageChange}
        />
      </div>
      <div className="d-flex flex-column row-gap-1">
        <div className="d-flex align-items-start gap-2">
          <CheckBox {...eSignProps} checked={eSign} disabled={!enableEsign} />
          <p className="text-base-regular color--mist mb-0">
            I agree to receive disclosures and communications electronically.
          </p>
        </div>
        <div className="d-flex align-items-start gap-2">
          <CheckBox {...agreeToTermsProps} checked={agreeToTerms} />
          <p className="text-base-regular color--mist mb-0">
            I agree to Stake's{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              End User Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
        {/* <div className="d-flex align-items-start gap-2">
          <CheckBox {...disclosuresProps} checked={disclosures} />
          <p className="text-base-regular color--mist mb-0">
            I confirm that I’ve read and agree to the{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Reg CC Disclosure
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Reg DD Disclosure
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Reg E Disclosure
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Notice (Reg P)
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              ACH Authorization & Agreement Disclosure
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Smart Card Account Agreement
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              Smart Card Security Agreement
            </a>
            ,{" "}
            <a
              className="terms-link"
              href="https://www.stakerent.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Smart Card Auto Payment Agreement
            </a>
            .
          </p>
        </div> */}
        {(eSignFieldError || agreeToTermsFieldError) && (
          <span className="text-xs-regular color--error-red">Please agree to all the terms and conditions above</span>
        )}
      </div>
    </div>
  );
};

function SSO({ setupStakeSaver = false, applyStakeChecking = false }) {
  const [step, setStep] = useState(1);

  const modalContext = useModalContext();

  const navigate = useNavigate();

  const { state: locationState } = useLocation();

  const { watch, handleSubmit } = useFormContext();

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const occupation = watch("occupation");
  const address = watch("streetAddress");
  const aptSuiteUnit = watch("aptSuiteUnit");
  const city = watch("city");
  const state = watch("state");
  const zipCode = watch("zipCode");
  const dob = watch("dob");
  const ssn = watch("ssn");
  const leaseStartDate = watch("leaseStartDate");
  const leaseEndDate = watch("leaseEndDate");
  const rentAmount = watch("rentAmount");

  const stepLabels = useMemo(() => {
    if (setupStakeSaver) {
      return ["Personal details", "Enrollment", "Disclosures"];
    } else if (applyStakeChecking) {
      return ["Personal details", "Disclosures"];
    }
    return [];
  }, [setupStakeSaver, applyStakeChecking]);

  //loading stake debit to load query function
  useStakeDebit();

  const { isLoading: isLoadingStakeSaverFormPrefill } = useStakeSaverFormPrefill();

  const { isLoading: isSettingUpStakeSaver, mutate: SetupStakeSaver } = useSetupStakeSaver({
    onSuccess: ({ data }) => {
      if (!data?.data?.is_kyc_approved) {
        renderFinishStakeSaverSetup(modalContext, { setupStakeSaverData: data?.data });
      } else {
        if (data?.data?.show_alert_message) {
          dispatchInfoToast(data?.data?.alert_message, { key: "setupSatkeSaver" });
        }
        renderStakeSaverSetupSuccess(modalContext);
      }
      navigate("/deals");
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "setupStakeSaver" }),
  });

  const { isLoading: isApplyingStakeChecking, mutate: ApplyStakeChecking } = useApplyStakeChecking({
    onSuccess: async ({ data }) => {
      if (locationState?.openStakePay2Modal) {
        renderSetupStakePay2(modalContext);
      }
      stakeCheckingStatusAction(getStakeDebitAPIObject(data?.data?.stake_checking_status));
      navigate(locationState?.previousPathname || -1);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "applyStakeChecking" }),
  });

  const onSubmit = async () => {
    if (step === 3 && setupStakeSaver) {
      SetupStakeSaver({
        sfid: getUserSfid(),
        firstname: firstName,
        lastname: lastName,
        address1: address,
        city,
        state,
        zipcode: zipCode,
        mobile_user_id: getUserId(),
        user_ip: await getPublicIPAddress(),
        ssn: replaceNonNumericCharacters(ssn),
        dob,
        occupation,
        aprt_or_unit: aptSuiteUnit,
        lease_start_date: leaseStartDate,
        lease_end_date: leaseEndDate,
        rent_amount: rentAmount?.unmasked,
      });
    } else if (step === 2 && applyStakeChecking) {
      ApplyStakeChecking({
        firstname: firstName,
        lastname: lastName,
        address1: address,
        city: city,
        state: state,
        zipcode: zipCode,
        ssn: replaceNonNumericCharacters(ssn),
        dob,
        occupation,
        aprt_or_unit: aptSuiteUnit,
      });
    } else {
      setStep((p) => p + 1);
    }
  };

  const isMutating = isSettingUpStakeSaver || isApplyingStakeChecking;

  return (
    <div className="onboarding-page sso-page">
      {isLoadingStakeSaverFormPrefill ? (
        <div className="onboarding-page loading-content">
          <CircularProgress />
        </div>
      ) : (
        <>
          <header className="bordered">
            <StakeLogo className="stake-logo" />
            <button className="back-button" onClick={navigate.bind(this, -1)}>
              <CrossMark />
            </button>
          </header>
          <div className="max-width-container">
            <h4 className="page-title">Let’s Set You Up</h4>
            <p className="page-description">We’ve pre-filled your info — make sure it’s all correct.</p>
            <form className="registration-form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <StepIndicator activeStep={step} hasLabels={true} steps={stepLabels.length} stepLabels={stepLabels} />
              {step === 1 ? (
                <PersonalDetails />
              ) : step === 2 && setupStakeSaver ? (
                <Enrollment />
              ) : (step === 2 && applyStakeChecking) || (step === 3 && setupStakeSaver) ? (
                <Disclosures />
              ) : (
                <></>
              )}
              <div className="d-flex align-items-center column-gap-4">
                {step > 1 && (
                  <button className="back-button" type="button" onClick={setStep.bind(this, (p) => p - 1)}>
                    <ArrowLeft />
                  </button>
                )}
                <PrimaryButton className="flex-grow-1" type="submit" disabled={isMutating}>
                  Continue {isMutating && <CircularProgress />}
                </PrimaryButton>
              </div>
              <div className="data-secure-label">
                <Lock />
                Your personal data is encrypted and stored securely
              </div>
            </form>
            <div className="data-secure-label mt-4 mb-7">
              <Lock />
              Your personal data is encrypted and stored securely
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const SSOWithFormProvider = withFormProvider(SSO, {
  defaultValues: async () => {
    try {
      const stakeSaverFormPrefillData = await APIhandler.stakeSaver.stakeSaverFormPrefill();
      const prefillInfo = stakeSaverFormPrefillData?.data?.data?.prefill_info;
      const isOutOfNetworkUser = stakeSaverFormPrefillData?.data?.data?.is_out_of_network_user;
      return {
        firstName: prefillInfo?.first_name ?? "",
        lastName: prefillInfo?.last_name ?? "",
        phoneNumber: transformCountryCodeFormatToUSNumber(prefillInfo?.phone ?? ""),
        email: prefillInfo?.email ?? "",
        streetAddress: prefillInfo?.address1 ?? "",
        aptSuiteUnit: prefillInfo?.address2 ?? "",
        city: prefillInfo?.city ?? "",
        state: prefillInfo?.state ?? "",
        zipCode: extract5DigitZipCode(prefillInfo?.zipcode),
        dob: !!prefillInfo?.dob ? moment(prefillInfo?.dob).format("YYYY-MM-DD") : "",
        ssn: !!prefillInfo?.ssn ? getFormattedSSN(prefillInfo?.ssn) : "",
        occupation: prefillInfo?.occupation,
        ...(isOutOfNetworkUser
          ? {
              leaseStartDate: !!prefillInfo?.lease_start_date
                ? moment(prefillInfo?.lease_start_date).format("YYYY-MM-DD")
                : "",
              leaseEndDate: !!prefillInfo?.lease_end_date
                ? moment(prefillInfo?.lease_end_date).format("YYYY-MM-DD")
                : "",
              rentAmount: !!prefillInfo?.rent_amount
                ? {
                    masked: formatCurrency(prefillInfo?.rent_amount)?.replace("$", ""),
                    unmasked: String(prefillInfo?.rent_amount),
                  }
                : undefined,
            }
          : {}),
      };
    } catch (e) {
      return {};
    }
  },
});

export default SSOWithFormProvider;
